import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
// CTSK-402 — Lucide icons replace raw glyphs (★/☆/✎/🕐/🔒) on the
// Watch toggle, Edit button, and tab strip. Imported directly (not
// via lib/icon-library.ts — that static map is purpose-built for
// tool-manifest-driven icon lookup by string name, not general page
// chrome) matching how other pages already import lucide-react icons
// straight from the package.
import {
  Star, Edit3, FileText, FileCode, Tags, History as HistoryIcon, Lock, Maximize2, Minimize2, Pin, PinOff,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import type {
  CustomProperty,
  CustomPropertyProvenance,
  CustomPropertyProvenanceEntry,
  Document,
  DocumentListItem,
  DocumentPatch,
  RelatedDocument,
  StorageContainer,
  Tag,
} from '@captain-tasko/shared';
import {
  DOCUMENT_LINK_CONTEXTS,
  DOCUMENT_LINK_LABELS,
  getBaselineType,
  type PreviewRenderer,
} from '@captain-tasko/shared';
import { apiFetch, apiList, apiSingle } from '../lib/api.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { SkeletonRows } from '../components/Skeleton.js';
import { toast } from '../components/Toast.js';
import { formatBytes } from '../lib/format.js';
import { LinkedWorkItemsPanel } from '../components/LinkedWorkItems.js';
import { FrontmatterPanel } from '../components/FrontmatterPanel.js';
import { MarkdownView } from '../components/MarkdownView.js';
import { VisibilityToggle } from '../components/VisibilityToggle.js';
import { ToolPalette } from '../components/ToolPalette.js';
import { FindingsPanel, type FindingsPanelData } from '../components/FindingsPanel.js';
import { AccessRequestSummaryCard } from '../components/AccessRequestSummaryCard.js';
import { MOCK_FINDINGS_DATA } from '../lib/findings-fixtures.js';
import { useToolActions } from '../hooks/useToolActions.js';
import { invokeToolAction, splitCompositeKey } from '../lib/tool-invoke.js';
import { ToolActionParamsModal } from '../components/ToolActionParamsModal.js';
import { CustomPropertyProvenanceChip } from '../components/CustomPropertyProvenanceChip.js';
import { SourceForToolChips } from '../components/SourceForToolChips.js';
import { CollapsiblePanel } from '../components/CollapsiblePanel.js';
import { useDetailDrawer } from '../hooks/useDetailDrawer.js';
import { DocumentGroupPanel } from '../components/DocumentGroupPanel.js';
import { NotesPanel } from '../components/NotesPanel.js';
import { TagAuditSection } from '../components/TagAuditSection.js';
import { AuditHistoryDrawer } from '../components/AuditHistoryDrawer.js';
import { ConfirmModal } from '../components/ConfirmModal.js';
import { useActorNames } from '../hooks/useActorNames.js';
import { useSession } from '../hooks/useSession.js';

type Tab = 'details' | 'content' | 'metadata' | 'activity' | 'permissions';

const TABS: Tab[] = ['details', 'content', 'metadata', 'activity', 'permissions'];

// CTSK-402 — Lucide equivalents for the tab strip (nearest-match per
// tab subject, no icon existed here before).
const TAB_ICONS: Record<Tab, typeof FileText> = {
  details: FileText,
  content: FileCode,
  metadata: Tags,
  activity: HistoryIcon,
  permissions: Lock,
};

interface DocumentActivity {
  id: string;
  actorId: string | null;
  action: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  message: string | null;
  timestamp: string;
}

interface SidecarBody {
  identity?: { title?: string; documentId?: string };
  frontmatter?: Record<string, unknown>;
  bodyExcerpt?: string;
  summary?: { short?: string; detailed?: string };
  classification?: { domain?: string[]; function?: string[]; platform?: string[] };
  contentProfile?: { wordCount?: number; readingTimeMinutes?: number };
  extraction?: { keyphrases?: string[]; urls?: string[]; tasks?: { raw: string; done: boolean }[] };
  llm?: { suggestedTags?: string[] };
  [k: string]: unknown;
}

export function DocumentDetail({ id, onNavigate }: { id: string; onNavigate: (p: string) => void }) {
  const { session } = useSession();
  const qc = useQueryClient();
  // CTSK-525: `?tab=activity` deep-links (used by the card "Post > Comment" action).
  const [tab, setTab] = useState<Tab>(() => {
    const t = new URLSearchParams(window.location.search).get('tab');
    return (TABS as readonly string[]).includes(t ?? '') ? (t as Tab) : 'details';
  });
  const [editing, setEditing] = useState(false);
  // CTSK-389 — History side-drawer (resource_audit timeline for this
  // document). A sibling affordance to the tab strip rather than a tab
  // itself — opening it doesn't change which primary tab is showing,
  // same "overlay on top of whatever you were looking at" behavior as
  // the FindingsPanel drawer below.
  const [historyOpen, setHistoryOpen] = useState(false);
  // CTSK-402 — delete confirm now routes through the app's ConfirmModal
  // instead of a native window.confirm() popup (inconsistent styling,
  // inconsistent keyboard behavior across browsers).
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  // CTSK-402 — roving-tabindex refs for the tab strip's keyboard nav
  // (WAI-ARIA APG tabs pattern: Arrow-Left/Right cycles, Home/End
  // jumps to first/last; only the active tab is in the Tab order).
  const tabButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
  function onTabKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    const currentIndex = TABS.indexOf(tab);
    let nextIndex: number | null = null;
    if (e.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TABS.length;
    else if (e.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
    else if (e.key === 'Home') nextIndex = 0;
    else if (e.key === 'End') nextIndex = TABS.length - 1;
    if (nextIndex === null) return;
    e.preventDefault();
    setTab(TABS[nextIndex]!);
    tabButtonRefs.current[nextIndex]?.focus();
  }
  // FindingsPanel state. null = closed. ?debugFindings=1 in the URL
  // opens with MOCK_FINDINGS_DATA so the UX can iterate without
  // hitting a tool. Live tool-invoke flow (see runInvoke below) also
  // pushes its result here.
  const [findings, setFindings] = useState<FindingsPanelData | null>(() => {
    if (typeof window === 'undefined') return null;
    const sp = new URLSearchParams(window.location.search);
    return sp.get('debugFindings') === '1' ? MOCK_FINDINGS_DATA : null;
  });
  // Composite-key of the action currently in flight (or null when
  // idle). Drives a 'running' toast + the FindingsPanel's rerunning
  // spinner. We don't gate the palette buttons themselves — the
  // operator should be able to cancel a long-running run by firing
  // a different action; only the last call's result wins.
  const [invoking, setInvoking] = useState<string | null>(null);
  // CTSK-121 — pending launch parameter form. Non-null only while an
  // invoke action WITH paramDefaults awaits operator confirmation;
  // actions without paramDefaults never open it (zero-click as ever).
  const [paramPrompt, setParamPrompt] = useState<{
    compositeKey: string;
    label: string;
    defaults: Record<string, unknown>;
  } | null>(null);
  // Last params actually used per composite key — FindingsPanel
  // re-runs reuse them instead of re-prompting.
  const lastParamsRef = useRef<Record<string, Record<string, unknown>>>({});

  // Installed-tool actions — used to resolve the composite key into a
  // friendly label when surfacing the FindingsPanel header.
  const { actions: toolActions } = useToolActions();

  /** Entry point from the palette + FindingsPanel re-run. Actions that
   *  declare paramDefaults open the pre-filled parameter form first
   *  (CTSK-121); everything else invokes immediately. Re-runs reuse
   *  the last-used params rather than prompting again. */
  function runInvoke(compositeKey: string) {
    const action = toolActions.find((a) => a.key === compositeKey);
    const defaults = action?.paramDefaults;
    const remembered = lastParamsRef.current[compositeKey];
    if (remembered) {
      void doInvoke(compositeKey, remembered);
      return;
    }
    if (defaults && Object.keys(defaults).length > 0) {
      setParamPrompt({
        compositeKey,
        label: action?.label ?? compositeKey,
        defaults,
      });
      return;
    }
    void doInvoke(compositeKey, undefined);
  }

  /** Fire a verb:'invoke' palette action through the server proxy
   *  and push the result into the FindingsPanel. The composite key
   *  shape (`<toolSlug>:<actionKey>`) matches what ToolPalette emits;
   *  unprefixed (mockup) keys are ignored — they were already
   *  handled inside the palette. */
  async function doInvoke(compositeKey: string, params: Record<string, unknown> | undefined) {
    const parts = splitCompositeKey(compositeKey);
    if (!parts) return;
    const { toolSlug, actionKey } = parts;
    const action = toolActions.find((a) => a.key === compositeKey);
    const friendlyLabel = action?.label ?? actionKey;
    if (params) lastParamsRef.current[compositeKey] = params;
    setInvoking(compositeKey);
    toast.info(`Running ${friendlyLabel}…`);
    try {
      const result = await invokeToolAction(toolSlug, actionKey, id, params ? { params } : {});
      setFindings({
        actionKey: compositeKey,
        actionLabel: friendlyLabel,
        result,
      });
      const n = result.findings?.length ?? 0;
      toast.success(
        `${friendlyLabel} done — ${n} finding${n === 1 ? '' : 's'}`,
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Invoke failed');
    } finally {
      setInvoking(null);
    }
  }

  const doc = useQuery({
    queryKey: ['document', id],
    queryFn: () => apiSingle<Document & { tagCount: number; workItemCount: number; etag: string }>(`/documents/${id}`),
  });

  const sidecar = useQuery({
    queryKey: ['sidecar', id],
    queryFn: () => apiSingle<{ body: SidecarBody; updatedAt: string | null }>(`/documents/${id}/sidecar`),
    enabled: !!doc.data,
  });

  const allTags = useQuery({
    queryKey: ['tags'],
    queryFn: () => apiList<Tag>('/tags'),
  });

  const containers = useQuery({
    queryKey: ['storage-containers'],
    queryFn: () => apiList<StorageContainer>('/storage-containers'),
  });

  const customProps = useQuery({
    queryKey: ['custom-properties'],
    queryFn: () => apiList<CustomProperty>('/custom-properties'),
  });

  // FR #198 — per-property provenance map for the chip on each
  // CustomPropertyField. Lazy-fetched: enabled only once the doc has
  // loaded so we don't waste a round trip during the initial doc
  // skeleton. Invalidated when the doc is patched so a fresh edit
  // immediately reflects "just now" in the chip.
  const cpProvenance = useQuery({
    queryKey: ['cp-provenance', id],
    queryFn: () =>
      apiSingle<CustomPropertyProvenance>(`/documents/${id}/custom-property-provenance`),
    enabled: !!doc.data,
    staleTime: 30_000,
  });

  const myContainer = useQuery({
    queryKey: ['document-container', id],
    queryFn: () => apiSingle<{ container: StorageContainer | null }>(`/documents/${id}/container`),
    enabled: !!doc.data,
  });

  // Only fetch text content for textual docs — PDFs/images embed directly via URL.
  const isTextual = doc.data ? isTextualMime(doc.data.mimeType) : false;
  const content = useQuery({
    queryKey: ['document-content', id],
    enabled: (tab === 'content' || tab === 'details') && isTextual,
    queryFn: async () => {
      const res = await fetch(`/api/v1/documents/${id}/content`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return res.text();
    },
  });

  const save = useMutation({
    mutationFn: (patch: DocumentPatch) =>
      apiSingle<Document>(`/documents/${id}`, {
        method: 'PATCH',
        body: patch,
        ifMatch: doc.data?.etag,
      }),
    onSuccess: () => {
      toast.success('Saved');
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['document', id] });
      qc.invalidateQueries({ queryKey: ['documents'] });
      // After a content patch the on-disk bytes change; refetch the
      // body so the rendered preview reflects the saved version.
      qc.invalidateQueries({ queryKey: ['document-content', id] });
      // FR #198 — a CP write produces a new audit row; refresh
      // provenance so the chip says "just now" right away.
      qc.invalidateQueries({ queryKey: ['cp-provenance', id] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // Saving content needs to surface the actual API error to the MarkdownView
  // (so its inline error banner renders) rather than just a toast.
  async function saveContent(next: string): Promise<void> {
    await apiSingle<Document>(`/documents/${id}`, {
      method: 'PATCH',
      body: { content: next },
      ifMatch: doc.data?.etag,
    });
    toast.success('Saved');
    qc.invalidateQueries({ queryKey: ['document', id] });
    qc.invalidateQueries({ queryKey: ['documents'] });
    qc.invalidateQueries({ queryKey: ['document-content', id] });
  }

  const del = useMutation({
    mutationFn: () => apiFetch<void>(`/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Document deleted');
      qc.invalidateQueries({ queryKey: ['documents'] });
      onNavigate('/documents');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  // ctsk-051 — Toggle the caller's watch state for this document.
  // Idempotent on the server (POST on already-watching is a no-op,
  // DELETE on not-watching same). Invalidates the detail key and the
  // list key so the Watching quick filter on the Documents page
  // stays in sync.
  const watch = useMutation({
    mutationFn: (next: boolean) =>
      apiSingle<{ documentId: string; isWatchedByMe: boolean; watcherCount: number }>(
        `/documents/${id}/watch`,
        { method: next ? 'POST' : 'DELETE' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document', id] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const addTag = useMutation({
    mutationFn: (tagId: string) =>
      apiSingle(`/documents/${id}/tags`, { method: 'POST', body: { tagId } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document', id] });
      qc.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeTag = useMutation({
    mutationFn: (tagId: string) =>
      apiFetch<void>(`/documents/${id}/tags/${tagId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['document', id] }),
  });

  const setContainer = useMutation({
    mutationFn: (containerId: string | null) =>
      apiSingle(`/documents/${id}/container`, { method: 'PUT', body: { containerId } }),
    onSuccess: () => {
      toast.success('Container updated');
      qc.invalidateQueries({ queryKey: ['document-container', id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Local editable fields
  const [editTitle, setEditTitle] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editAsn, setEditAsn] = useState<number | ''>('');
  useEffect(() => {
    if (doc.data) {
      setEditTitle(doc.data.title);
      setEditDate(doc.data.date ? doc.data.date.slice(0, 10) : '');
      setEditAsn(doc.data.archiveSerialNumber ?? '');
    }
  }, [doc.data, editing]);

  if (doc.isLoading) return <SkeletonRows rows={10} />;
  if (doc.isError || !doc.data) return <div>Document not found.</div>;

  const d = doc.data;
  const docTags = d.tags ?? [];
  const availableTags = (allTags.data?.data ?? []).filter((t) => !docTags.some((dt) => dt.id === t.id));

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <h1 className="page-title" style={{ margin: 0 }}>{d.title}</h1>
            <VisibilityToggle
              resourcePath="/documents"
              resourceId={d.id}
              current={d.visibility ?? 'private'}
              resourceLabel="document"
              canShare={session?.capabilities?.includes('documents:share') ?? false}
              invalidateKeys={[['documents'], ['document', d.id]]}
            />
            {/* CTSK-365 — "Source for: {tool}" backlink chips. Only
                present when the API says this doc is a tool's registry
                file; absent list renders nothing (no dead affordance). */}
            <SourceForToolChips tools={d.referencedByTools} onNavigate={onNavigate} />
          </div>
          <div className="page-subtitle">
            <code style={{ fontFamily: 'var(--font-mono)' }}>{d.id}</code>
          </div>
        </div>
        {/* Action row — two visually distinct groups side by side
            (FR 2026-05-29 Tool Palette UX iteration on the mockup).
            Left group: Tool Palette (smaller buttons, lighter tint).
            Right group: primary actions (Watch / Download / Edit /
            Delete) at normal .btn size, darker tint. align-items
            centered so the two groups vertically line up across the
            size delta; flex-wrap so the row breaks gracefully on a
            narrow viewport (palette group drops below the primary
            group instead of pushing it off-screen). */}
        <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <div className="action-group action-group--palette" aria-label="Document tools">
            <ToolPalette documentId={d.id} onCommand={runInvoke} />
          </div>
          <div className="action-group action-group--primary" aria-label="Primary document actions">
            {/* ctsk-051 — Watch toggle. Mirrors WorkItemDetail's button:
                star fills when watching; behaves identically since the
                backend contract is the same. */}
            <button
              type="button"
              className="btn"
              onClick={() => watch.mutate(!d.isWatchedByMe)}
              disabled={watch.isPending}
              title={d.isWatchedByMe
                ? 'Stop watching this document'
                : 'Watch this document — appears in your Watching quick filter on the Documents page'}
              aria-pressed={d.isWatchedByMe ?? false}
              style={{
                background: d.isWatchedByMe
                  ? 'color-mix(in srgb, var(--accent-primary) 12%, var(--surface-raised))'
                  : undefined,
                borderColor: d.isWatchedByMe ? 'var(--accent-primary)' : undefined,
                color: d.isWatchedByMe ? 'var(--accent-primary)' : undefined,
                fontWeight: d.isWatchedByMe ? 600 : undefined,
              }}
            >
              {/* CTSK-402 — Lucide Star, filled/unfilled via `fill`
                  rather than swapping Star/StarOff: a filled-vs-outline
                  star reads as "watching" state at a glance the same
                  way the ★/☆ glyphs did, without the mismatched
                  connotation StarOff (usually a destructive/remove
                  action icon) would carry for "not watching yet". */}
              <Star size={14} strokeWidth={1.5} fill={d.isWatchedByMe ? 'currentColor' : 'none'} aria-hidden="true" />
              {d.isWatchedByMe ? ' Watching' : ' Watch'}
            </button>
            {/* CTSK-402 — `download` attribute (mirroring PreviewPane's
                Content-tab download link below) so the browser prompts
                a save dialog instead of opening the raw stream in a
                new tab. Passing the actual filename (rather than a
                bare `download`) gives the saved file a sensible name
                even when the response's Content-Disposition doesn't. */}
            <a
              className="btn"
              href={`/api/v1/documents/${id}/content`}
              download={d.originalFilename || d.title}
            >
              Download
            </a>
            {!editing && (
              <button className="btn btn--primary" onClick={() => setEditing(true)}>
                <Edit3 size={14} strokeWidth={1.5} aria-hidden="true" /> Edit
              </button>
            )}
            {editing && (
              <>
                <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
                <button
                  className="btn btn--primary"
                  disabled={save.isPending}
                  onClick={() =>
                    save.mutate({
                      title: editTitle,
                      date: editDate ? new Date(editDate).toISOString() : null,
                      archiveSerialNumber: typeof editAsn === 'number' ? editAsn : null,
                    })
                  }
                >
                  {save.isPending ? 'Saving…' : 'Save changes'}
                </button>
              </>
            )}
            <button
              className="btn btn--danger"
              onClick={() => setDeleteConfirmOpen(true)}
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete document"
        message={`Delete "${d.title}"? This can be undone from the Recycle Bin until it's purged.`}
        confirmLabel="Delete"
        danger
        isPending={del.isPending}
        onConfirm={() => { setDeleteConfirmOpen(false); del.mutate(); }}
        onCancel={() => setDeleteConfirmOpen(false)}
      />

      {/* CTSK-170 Part B/8 — Access-request summary card. Guards on
          the `nest/external-clients` tag internally; non-access docs
          get a no-op render. Sits above the tab strip so approval
          state is always visible without needing to click into a
          specific tab. */}
      <AccessRequestSummaryCard
        tagNames={(d.tags ?? []).map((t) => t.name)}
        customProperties={(d.customProperties ?? {}) as Record<string, unknown>}
        onNavigate={onNavigate}
      />

      <div className="tabs" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-1)' }}>
        {/* CTSK-402 — WAI-ARIA APG tabs pattern: role="tablist" on the
            container, role="tab" + aria-selected + aria-controls on
            each button, roving tabindex (only the active tab sits in
            the Tab order; Arrow-Left/Right/Home/End move focus + move
            selection together, per onTabKeyDown above). */}
        <div role="tablist" aria-label="Document sections" style={{ display: 'flex' }} onKeyDown={onTabKeyDown}>
          {TABS.map((t, i) => {
            const Icon = TAB_ICONS[t];
            const active = tab === t;
            return (
              <button
                key={t}
                ref={(el) => { tabButtonRefs.current[i] = el; }}
                id={`docdetail-tab-${t}`}
                role="tab"
                aria-selected={active}
                aria-controls={`docdetail-panel-${t}`}
                tabIndex={active ? 0 : -1}
                className={clsx('tab', active && 'active')}
                onClick={() => setTab(t)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}
              >
                <Icon size={14} strokeWidth={1.5} aria-hidden="true" />
                {t}
              </button>
            );
          })}
        </div>
        {/* CTSK-389 — History affordance: opens the resource_audit
            timeline for this document in a side-drawer, gated
            server-side on audit:view (the drawer itself renders a 403
            hint rather than erroring if the caller lacks the cap).
            CTSK-402 — the stray `marginRight: 4` inline hack that used
            to live on this button is gone; spacing from the tab list
            now comes from the `.tabs` container's `gap` token above. */}
        <button
          type="button"
          className="btn"
          onClick={() => setHistoryOpen(true)}
          style={{ fontSize: 12, padding: '4px 10px' }}
        >
          <HistoryIcon size={14} strokeWidth={1.5} aria-hidden="true" /> History
        </button>
      </div>
      {historyOpen && (
        <AuditHistoryDrawer resourceType="document" resourceId={id} onClose={() => setHistoryOpen(false)} />
      )}

      {tab === 'details' && (
        <div role="tabpanel" id="docdetail-panel-details" aria-labelledby="docdetail-tab-details">
        <DetailsTab
          doc={d}
          docTags={docTags}
          editing={editing}
          editTitle={editTitle}
          setEditTitle={setEditTitle}
          editDate={editDate}
          setEditDate={setEditDate}
          editAsn={editAsn}
          setEditAsn={setEditAsn}
          availableTags={availableTags}
          onAddTag={(t) => addTag.mutate(t)}
          onRemoveTag={(t) => removeTag.mutate(t)}
          sidecar={sidecar.data?.body}
          contentPreview={content.data}
          onSaveContent={saveContent}
          containers={containers.data?.data ?? []}
          currentContainerId={myContainer.data?.container?.id ?? null}
          onContainerChange={(cid) => setContainer.mutate(cid)}
          // 0.1.30 (Stage 4a) — scope filter now respects baseline-type
          // binding in precedence to tag binding. A custom property shows
          // on this doc iff:
          //   1. `appliesToTypes` is non-empty AND includes the doc's
          //      `baselineType.id` — TYPE BINDING wins outright. (The
          //      structural fix for the bug class behind #275 / #277.)
          //   2. Otherwise, `appliesToTags` is empty (org-wide default)
          //      OR the doc carries at least one tag whose NAME matches
          //      one of the property's `appliesToTags` entries.
          //
          // Why precedence not union: a bundle that has migrated to types
          // should be the authoritative source. Mixing it with tag scoping
          // would re-introduce the leak we're trying to eliminate.
          customProperties={(customProps.data?.data ?? []).filter((p) => {
            const appliesToTypes = p.appliesToTypes ?? [];
            if (appliesToTypes.length > 0) {
              return d.baselineType?.id ? appliesToTypes.includes(d.baselineType.id) : false;
            }
            // Fall back to legacy tag-binding.
            if (!p.appliesToTags || p.appliesToTags.length === 0) return true;
            const docTagNames = new Set((d.tags ?? []).map((t) => t.name));
            return p.appliesToTags.some((name) => docTagNames.has(name));
          })}
          customPropertyValues={(d.customProperties ?? {}) as Record<string, unknown>}
          customPropertyProvenance={cpProvenance.data?.entries ?? {}}
          onCustomPropertyChange={(values) => save.mutate({ customProperties: values })}
          documentId={id}
          onNavigate={onNavigate}
        />
        </div>
      )}
      {tab === 'content' && (
        <div role="tabpanel" id="docdetail-panel-content" aria-labelledby="docdetail-tab-content">
          <ContentTab id={id} mime={d.mimeType} title={d.title} content={content.data} onSaveContent={saveContent} />
        </div>
      )}
      {tab === 'metadata' && (
        <div role="tabpanel" id="docdetail-panel-metadata" aria-labelledby="docdetail-tab-metadata">
          <MetadataTab doc={d} sidecar={sidecar.data?.body} />
        </div>
      )}
      {tab === 'activity' && (
        <div role="tabpanel" id="docdetail-panel-activity" aria-labelledby="docdetail-tab-activity">
          <TagAuditSection documentId={id} />
          <ActivityTab id={id} />
        </div>
      )}
      {tab === 'permissions' && (
        <div role="tabpanel" id="docdetail-panel-permissions" aria-labelledby="docdetail-tab-permissions">
          <PermissionsTab doc={d} />
        </div>
      )}

      {/* FindingsPanel slide-in drawer (FR 2026-05-31 §FindingsPanel).
          Renders tool invocation results (Finding[] + meta) from any
          verb:'invoke' action. Two entry points:
            1. ?debugFindings=1 — opens with MOCK_FINDINGS_DATA so the
               UX iterates without a live tool.
            2. Tool Palette button → runInvoke → setFindings(result).
          Re-run button hits the same runInvoke handler so the panel
          and the palette share dispatch. */}
      <FindingsPanel
        data={findings}
        onClose={() => setFindings(null)}
        onRerun={(actionKey) => { runInvoke(actionKey); }}
        rerunning={invoking !== null && findings !== null && invoking === findings.actionKey}
      />

      {/* CTSK-121 — launch parameter form for invoke actions that
          declare paramDefaults. Pre-filled with the manifest values;
          the operator can tweak before running or cancel out. */}
      {paramPrompt && (
        <ToolActionParamsModal
          actionLabel={paramPrompt.label}
          paramDefaults={paramPrompt.defaults}
          onRun={(params) => {
            const key = paramPrompt.compositeKey;
            setParamPrompt(null);
            void doInvoke(key, params);
          }}
          onCancel={() => setParamPrompt(null)}
        />
      )}
    </>
  );
}

function ActivityTab({ id }: { id: string }) {
  const activity = useQuery({
    queryKey: ['document-activity', id],
    queryFn: () => apiList<DocumentActivity>(`/documents/${id}/activity`),
  });
  // CTSK-402 — resolve actorId ULIDs to display names. Must run every
  // render (hooks can't sit after the early returns below).
  const actorNames = useActorNames(activity.data?.data.map((a) => a.actorId) ?? []);

  if (activity.isLoading) return <SkeletonRows rows={5} />;
  if (activity.isError) return <p style={{ color: 'var(--danger-primary)' }}>Couldn't load activity.</p>;
  if (!activity.data || activity.data.data.length === 0) {
    return <p style={{ color: 'var(--text-muted)' }}>No activity recorded for this document yet.</p>;
  }

  return (
    <div style={{ background: 'var(--surface-raised)', padding: 'var(--space-5)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
      <h3 style={{ marginTop: 0 }}>History</h3>
      <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
        Every change to this document — metadata edits, tag adds/removes, container assignments, deletes, restores.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {activity.data.data.map((a) => (
          <div key={a.id} style={{ borderLeft: '2px solid var(--accent-primary)', paddingLeft: 'var(--space-3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
              <span><code style={{ fontFamily: 'var(--font-mono)' }}>{formatAction(a.action)}</code> by {actorDisplayName(a.actorId, actorNames)}</span>
              <span>{new Date(a.timestamp).toLocaleString()}</span>
            </div>
            {a.message && <div style={{ marginTop: 'var(--space-1)' }}>{a.message}</div>}
            {(a.before || a.after) && (
              <details style={{ marginTop: 6 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-muted)' }}>Show change</summary>
                <ChangeDiff before={a.before} after={a.after} />
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// CTSK-402 — never render the raw ULID: null actor is the system,
// a resolved actor is its display name, anything else (still
// resolving, or resolved-to-null meaning unresolvable/no-cap) reads
// as "unknown user" rather than leaking the opaque id.
function actorDisplayName(actorId: string | null, names: Record<string, string | null>): string {
  if (!actorId) return 'system';
  return names[actorId] ?? 'unknown user';
}

function formatAction(action: string): string {
  // "document.metadata.update" → "Metadata update", "document.tag_add" → "Tag add"
  return action
    .replace(/^document\./, '')
    .replace(/_/g, ' ')
    .replace(/\./g, ' / ')
    .replace(/^./, (c) => c.toUpperCase());
}

function ChangeDiff({ before, after }: { before: Record<string, unknown> | null; after: Record<string, unknown> | null }) {
  const fields = new Set<string>([
    ...Object.keys(before ?? {}),
    ...Object.keys(after ?? {}),
  ]);
  // Filter to changed fields only
  const changed = [...fields].filter((f) => JSON.stringify((before ?? {})[f]) !== JSON.stringify((after ?? {})[f]));
  if (changed.length === 0) {
    return <pre style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'pre-wrap', marginTop: 6 }}>
      {JSON.stringify({ before, after }, null, 2)}
    </pre>;
  }
  return (
    <table style={{ marginTop: 6, fontSize: 12, width: '100%' }}>
      <thead>
        <tr>
          <th style={{ textAlign: 'left', color: 'var(--text-muted)' }}>Field</th>
          <th style={{ textAlign: 'left', color: 'var(--text-muted)' }}>Before</th>
          <th style={{ textAlign: 'left', color: 'var(--text-muted)' }}>After</th>
        </tr>
      </thead>
      <tbody>
        {changed.map((f) => (
          <tr key={f}>
            <td><code style={{ fontFamily: 'var(--font-mono)' }}>{f}</code></td>
            <td style={{ color: 'var(--danger-primary)' }}><code style={{ fontFamily: 'var(--font-mono)' }}>{JSON.stringify((before ?? {})[f])}</code></td>
            <td style={{ color: 'var(--success-primary)' }}><code style={{ fontFamily: 'var(--font-mono)' }}>{JSON.stringify((after ?? {})[f])}</code></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DetailsTab({
  doc, docTags, editing, editTitle, setEditTitle, editDate, setEditDate, editAsn, setEditAsn,
  availableTags, onAddTag, onRemoveTag, sidecar, contentPreview, onSaveContent,
  containers, currentContainerId, onContainerChange,
  customProperties, customPropertyValues, customPropertyProvenance, onCustomPropertyChange,
  documentId, onNavigate,
}: {
  doc: DocumentListItem;
  docTags: Tag[];
  editing: boolean;
  editTitle: string; setEditTitle: (v: string) => void;
  editDate: string; setEditDate: (v: string) => void;
  editAsn: number | ''; setEditAsn: (v: number | '') => void;
  availableTags: Tag[];
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  sidecar?: SidecarBody;
  contentPreview?: string;
  onSaveContent: (next: string) => Promise<void>;
  containers: StorageContainer[];
  currentContainerId: string | null;
  onContainerChange: (id: string | null) => void;
  customProperties: CustomProperty[];
  customPropertyValues: Record<string, unknown>;
  customPropertyProvenance: Record<string, CustomPropertyProvenanceEntry>;
  onCustomPropertyChange: (next: Record<string, unknown>) => void;
  documentId: string;
  onNavigate: (p: string) => void;
}) {
  // CTSK-522 — the whole panel stack (left column) collapses behind a rail
  // on its right edge, facing the preview. The stack stays mounted (hidden)
  // so in-flight edits and per-panel state survive a toggle.
  const drawer = useDetailDrawer();
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: drawer.open ? '360px 12px minmax(0, 1fr)' : '12px minmax(0, 1fr)',
        columnGap: 'var(--space-3)',
      }}
    >
      <div
        id="doc-detail-drawer"
        data-testid="doc-detail-drawer"
        style={{ display: drawer.open ? 'flex' : 'none', flexDirection: 'column', gap: 'var(--space-4)' }}
      >
        {/* Properties */}
        <CollapsiblePanel storageKey="docdetail:properties" title="Properties">
          <dl className="kv">
            <dt>Title</dt>
            <dd>
              {editing ? (
                <input
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  style={{ width: '100%', padding: '4px 8px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)' }}
                />
              ) : (
                doc.title
              )}
            </dd>
            <dt>Date</dt>
            <dd>
              {editing ? (
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  style={{ padding: '4px 6px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)' }}
                />
              ) : (
                doc.date ? new Date(doc.date).toLocaleDateString() : <span style={{ color: 'var(--text-muted)' }}>—</span>
              )}
            </dd>
            <dt>ASN</dt>
            <dd>
              {editing ? (
                <input
                  type="number"
                  value={editAsn === '' ? '' : String(editAsn)}
                  onChange={(e) => setEditAsn(e.target.value === '' ? '' : Number(e.target.value))}
                  style={{ padding: '4px 6px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-sm)', width: 100 }}
                />
              ) : (
                doc.archiveSerialNumber ?? <span style={{ color: 'var(--text-muted)' }}>—</span>
              )}
            </dd>
            <dt>Intake</dt><dd>{doc.intakeClass}</dd>
            <dt>Status</dt><dd><StatusBadge status={doc.status} variant="document" /></dd>
            <dt>Health</dt><dd><StatusBadge status={doc.health} variant="health" /></dd>
            <dt>Type</dt><dd>{doc.mimeType}</dd>
            <dt>Size</dt><dd>{formatBytes(doc.sizeBytes)}</dd>
            <dt>Pages</dt><dd>{doc.pageCount ?? <span style={{ color: 'var(--text-muted)' }}>—</span>}</dd>
            <dt>Created</dt><dd>{new Date(doc.createdAt).toLocaleString()}</dd>
            <dt>Updated</dt><dd>{new Date(doc.updatedAt).toLocaleString()}</dd>
          </dl>
        </CollapsiblePanel>

        {/* Tags (ctsk-058) — read-only by default; small ✎ unlocks it.
            When unlocked, shows a type-ahead search over available tags
            instead of a flat dropdown so finding "site/chadwiki" in a
            large taxonomy is instant. */}
        <TagsSection
          docTags={docTags}
          availableTags={availableTags}
          onAddTag={onAddTag}
          onRemoveTag={onRemoveTag}
          pageEditing={editing}
        />

        {/* Storage container (ctsk-057) — read-only by default; small
            ✎ unlocks it so an accidental drag-click on the select doesn't
            silently re-route the doc. */}
        <ContainerSection
          containers={containers}
          currentContainerId={currentContainerId}
          onContainerChange={onContainerChange}
          pageEditing={editing}
        />

        {/* CTSK-498 — Group / Collection + collapsible Related documents (flag-gated inside). */}
        <DocumentGroupPanel doc={doc} />

        {/* Frontmatter — smart renderer that collapses bulky values
            (_content_plain, etc.) so the panel stays scannable. */}
        {sidecar?.frontmatter && Object.keys(sidecar.frontmatter).length > 0 && (
          <FrontmatterPanel data={sidecar.frontmatter} />
        )}

        {/* Custom properties — default-collapsed per drthe0 2026-06-03
            to reduce noise on docs that don't need CP editing every
            visit. localStorage remembers if the operator pins it open. */}
        {customProperties.length > 0 && (
          <CollapsiblePanel
            storageKey="docdetail:custom-properties"
            title="Custom properties"
            badge={`${customProperties.length} field${customProperties.length === 1 ? '' : 's'}`}
            defaultCollapsed
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {customProperties.map((p) => (
                <CustomPropertyField
                  key={p.id}
                  property={p}
                  value={customPropertyValues[p.key]}
                  provenance={customPropertyProvenance[p.key]}
                  onChange={(next) => {
                    const updated = { ...customPropertyValues, [p.key]: next };
                    if (next === null || next === undefined || next === '') delete updated[p.key];
                    onCustomPropertyChange(updated);
                  }}
                />
              ))}
            </div>
          </CollapsiblePanel>
        )}

        {/* Linked work items */}
        <LinkedWorkItemsPanel documentId={documentId} onNavigate={onNavigate} />

        {/* ctsk-062 — Linked documents (doc to doc linking). Sits
            immediately under Linked work items so the two "what's
            connected to this doc" panels live together. Edit-gate
            pattern from tags/containers above; empty state collapses
            to a small "Add link" affordance so the section earns its
            space only after the first link lands. */}
        <LinkedDocumentsSection documentId={documentId} onNavigate={onNavigate} />

        {/* CTSK-509 — post-it notes on this document (hidden when enableNotes is off). */}
        <NotesPanel targetType="document" targetId={documentId} onNavigate={onNavigate} />

        {/* 0.1.24 Stage 2c — Crawl tree widget. Renders only when
            this doc has parent_of outbound links (i.e. it's a
            crawl seed or interior parent page). Walks the
            recursive endpoint and shows the hierarchy as nested
            list items. Each row navigates on click. */}
        <CrawlTreeSection documentId={documentId} onNavigate={onNavigate} />

        {/* Pipeline output */}
        {sidecar && (sidecar.summary?.short || (sidecar.extraction?.keyphrases?.length ?? 0) > 0 || sidecar.classification) && (
          <CollapsiblePanel storageKey="docdetail:pipeline-output" title="Pipeline output">
            {sidecar.summary?.short && (
              <>
                <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>Summary</div>
                <p style={{ marginTop: 'var(--space-1)' }}>{sidecar.summary.short}</p>
              </>
            )}
            {sidecar.extraction?.keyphrases && sidecar.extraction.keyphrases.length > 0 && (
              <>
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 'var(--space-2)' }}>Keyphrases</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)' }}>
                  {sidecar.extraction.keyphrases.slice(0, 12).map((k) => (
                    <span key={k} className="tag-chip">{k}</span>
                  ))}
                </div>
              </>
            )}
            {sidecar.classification && (
              <>
                {sidecar.classification.domain && sidecar.classification.domain.length > 0 && (
                  <>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 'var(--space-2)' }}>Domain</div>
                    <div>{sidecar.classification.domain.join(', ')}</div>
                  </>
                )}
                {sidecar.classification.function && sidecar.classification.function.length > 0 && (
                  <>
                    <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 'var(--space-2)' }}>Function</div>
                    <div>{sidecar.classification.function.join(', ')}</div>
                  </>
                )}
              </>
            )}
            {sidecar.contentProfile && (
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 'var(--space-2)' }}>
                {sidecar.contentProfile.wordCount} words · {sidecar.contentProfile.readingTimeMinutes} min read
              </div>
            )}
          </CollapsiblePanel>
        )}

      </div>

      {/* CTSK-522 — drawer rail. */}
      <button
        type="button"
        data-testid="doc-detail-drawer-toggle"
        onClick={drawer.toggle}
        aria-expanded={drawer.open}
        aria-controls="doc-detail-drawer"
        aria-label={drawer.open ? 'Collapse details panel' : 'Expand details panel'}
        title={drawer.open ? 'Collapse details panel' : 'Expand details panel'}
        className="doc-drawer-rail"
      >
        {/* A short column of chevrons down the rail's length (not one at the
            top) so the toggle reads as a control beside any panel. */}
        {Array.from({ length: 4 }, (_, i) => (
          drawer.open
            ? <ChevronLeft key={i} size={9} strokeWidth={2.5} aria-hidden />
            : <ChevronRight key={i} size={9} strokeWidth={2.5} aria-hidden />
        ))}
      </button>

      {/* Preview pane */}
      <PreviewPane
        documentId={doc.id}
        mimeType={doc.mimeType}
        title={doc.title}
        textContent={contentPreview}
        onSaveContent={onSaveContent}
      />
    </div>
  );
}

// ─── Edit-icon gating (ctsk-057/058) ─────────────────────────────────
//
// Two sidebar sections — Tags and Storage container — used to be
// editable inline at all times. A stray click on the select silently
// re-routed the doc, and dragging across the chip list could remove a
// tag by accident. Both sections are now read-only by default; a tiny
// ✎ icon in the section header unlocks the controls. The page-level
// Edit button still opens everything in one shot (its `editing`
// prop is OR'd with the local unlock).
//
// Pattern is intentionally shallow — two sections, two local hooks,
// no new shared component. If a third gated section ever lands the
// inline duplication is the moment to extract <EditGate>.

function SectionHeader({
  title,
  showEditIcon,
  unlocked,
  onUnlock,
  onLock,
}: {
  title: string;
  showEditIcon: boolean;
  unlocked: boolean;
  onUnlock: () => void;
  onLock: () => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 0, marginBottom: 'var(--space-2)' }}>
      <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {title}
      </h3>
      {showEditIcon && (
        unlocked ? (
          <button
            type="button"
            onClick={onLock}
            aria-label={`Lock ${title.toLowerCase()}`}
            title="Done editing"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--accent-primary)',
              cursor: 'pointer',
              fontSize: 12,
              padding: '2px 4px',
              fontWeight: 600,
            }}
          >
            Done
          </button>
        ) : (
          <button
            type="button"
            onClick={onUnlock}
            aria-label={`Edit ${title.toLowerCase()}`}
            title={`Edit ${title.toLowerCase()}`}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              fontSize: 13,
              padding: '2px 4px',
              lineHeight: 1,
            }}
          >
            ✎
          </button>
        )
      )}
    </div>
  );
}

function TagsSection({
  docTags,
  availableTags,
  onAddTag,
  onRemoveTag,
  pageEditing,
}: {
  docTags: Tag[];
  availableTags: Tag[];
  onAddTag: (tagId: string) => void;
  onRemoveTag: (tagId: string) => void;
  pageEditing: boolean;
}) {
  const [unlocked, setUnlocked] = useState(false);
  const isEditable = pageEditing || unlocked;
  const [tagSearch, setTagSearch] = useState('');

  // ctsk-058 — type-ahead filter over name + description. Case-insensitive.
  const filteredAvailable = useMemo(() => {
    if (!tagSearch.trim()) return availableTags;
    const needle = tagSearch.toLowerCase();
    return availableTags.filter(
      (t) => t.name.toLowerCase().includes(needle)
        || (t.description ?? '').toLowerCase().includes(needle),
    );
  }, [availableTags, tagSearch]);

  return (
    <section style={{ background: 'var(--surface-raised)', padding: 'var(--space-4)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
      <SectionHeader
        title="Tags"
        showEditIcon={!pageEditing}
        unlocked={unlocked}
        onUnlock={() => setUnlocked(true)}
        onLock={() => { setUnlocked(false); setTagSearch(''); }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', marginBottom: 'var(--space-2)' }}>
        {docTags.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>No tags yet.</span>}
        {docTags.map((t) => (
          <span key={t.id} className="tag-chip" style={{ paddingRight: isEditable ? 4 : 8 }}>
            <span className="tag-chip__dot" style={{ background: t.color }} />
            {t.name}
            {isEditable && (
              <button
                onClick={() => onRemoveTag(t.id)}
                aria-label={`Remove tag ${t.name}`}
                style={{ padding: '0 4px', fontSize: 11, lineHeight: 1, background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {isEditable && availableTags.length > 0 && (
        <>
          <input
            type="search"
            placeholder="Search tags to add…"
            value={tagSearch}
            onChange={(e) => setTagSearch(e.target.value)}
            autoFocus={unlocked}
            style={{
              width: '100%',
              padding: '6px 10px',
              border: '1px solid var(--border-strong)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 13,
              marginBottom: 6,
            }}
          />
          <div style={{ display: 'flex', flexDirection: 'column', maxHeight: 180, overflowY: 'auto', gap: 2 }}>
            {filteredAvailable.length === 0 && (
              <span style={{ color: 'var(--text-muted)', fontSize: 12, padding: '4px 8px' }}>
                {availableTags.length === 0 ? 'No tags to add.' : `No tag matches "${tagSearch}".`}
              </span>
            )}
            {filteredAvailable.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => { onAddTag(t.id); setTagSearch(''); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '4px 8px',
                  fontSize: 13,
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  borderRadius: 3,
                  color: 'var(--text-primary)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-sunken)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <span className="tag-chip__dot" style={{ background: t.color, flexShrink: 0 }} />
                <span>{t.name}</span>
                {t.description && (
                  <span style={{ color: 'var(--text-muted)', fontSize: 11, marginLeft: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>
                    {t.description}
                  </span>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}

function ContainerSection({
  containers,
  currentContainerId,
  onContainerChange,
  pageEditing,
}: {
  containers: StorageContainer[];
  currentContainerId: string | null;
  onContainerChange: (id: string | null) => void;
  pageEditing: boolean;
}) {
  const [unlocked, setUnlocked] = useState(false);
  const isEditable = pageEditing || unlocked;
  const currentContainer = containers.find((c) => c.id === currentContainerId);

  return (
    <section style={{ background: 'var(--surface-raised)', padding: 'var(--space-4)', borderRadius: 'var(--radius-md)', border: '1px solid var(--border-subtle)' }}>
      <SectionHeader
        title="Storage container"
        showEditIcon={!pageEditing}
        unlocked={unlocked}
        onUnlock={() => setUnlocked(true)}
        onLock={() => setUnlocked(false)}
      />
      {isEditable ? (
        <select
          value={currentContainerId ?? ''}
          onChange={(e) => onContainerChange(e.target.value || null)}
          style={{ width: '100%', padding: 'var(--space-1)', fontSize: 13 }}
        >
          <option value="">— None —</option>
          {containers.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      ) : currentContainer ? (
        // 0.1.27 — container chip uses the container's defined color
        // (set in Storage admin). Squarish radius distinguishes it
        // from tag chips (which use a tiny colored dot + pill shape).
        // Soft background tint + solid border + solid color tick give
        // the chip a single coherent color signature at a glance.
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            padding: '4px 10px',
            border: `1px solid ${currentContainer.color ?? 'var(--border-subtle)'}`,
            background: currentContainer.color ? `${currentContainer.color}1f` : 'transparent',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--text-primary)',
          }}
        >
          <span
            aria-hidden
            style={{
              width: 10,
              height: 10,
              borderRadius: 2,
              background: currentContainer.color ?? 'var(--text-muted)',
              flexShrink: 0,
            }}
          />
          {currentContainer.name}
        </div>
      ) : (
        <div
          style={{
            padding: '6px 10px',
            border: '1px solid transparent',
            borderRadius: 'var(--radius-sm)',
            fontSize: 13,
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          — None —
        </div>
      )}
      {containers.length === 0 && (
        <p style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 'var(--space-2)' }}>
          No containers yet. Create one under Storage in the nav.
        </p>
      )}
    </section>
  );
}

/**
 * 0.1.29 — AI-prompt-injection warning chip.
 *
 * Renders when the doc's baseline type flags `aiPromptInjectionGuard`
 * (HTML, markdown, plain text, JSON, YAML, XML, CSV, NDJSON, JS).
 * The chip is the *visible* half of the AI guard; the LLM-call-site
 * gate (Slice 4) is the structural half, landing in a follow-up.
 *
 * Intentionally informational, not blocking — operators need to see
 * it but should still be able to view the doc. The decision to feed
 * the content into an LLM panel is what the structural gate will
 * actually intercept.
 */
function AiGuardChip() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: '6px 12px',
        margin: '0 16px 8px',
        background: '#fff7e6',
        border: '1px solid #e0b97f',
        borderRadius: 'var(--radius-sm)',
        fontSize: 12,
        color: '#7a4a14',
      }}
      role="note"
      aria-label="AI prompt-injection warning"
    >
      <span aria-hidden style={{ fontSize: 13 }}>⚠</span>
      <span>
        <strong>AI guard:</strong> this document may contain text that an AI
        system could interpret as instructions. Review before feeding into
        chunker / summarizer / classifier panels.
      </span>
    </div>
  );
}

/**
 * 0.1.33 — Top-right button-bar pattern used by the source/preview
 * toggle on heavy renderers (PDF, image, docx-html). Matches the
 * `htmlMode` toggle UX from 0.1.27 so all heavy renderers feel the
 * same — small `btn--ghost btn--sm` aligned right, sitting in a
 * thin header strip above the content area.
 *
 * `nudge` is the small inline label rendered next to the button
 * when the toggle is in source mode ("PDF preview hidden", etc.).
 * It's optional; preview-mode usage just hides the nudge.
 */
function RenderModeBar({
  mode,
  nudge,
  onToggle,
}: {
  mode: 'source' | 'preview';
  nudge?: string;
  onToggle: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 12px',
        borderBottom: '1px solid var(--border-subtle)',
      }}
    >
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {mode === 'source' && nudge ? nudge : ''}
      </span>
      <button
        className="btn btn--ghost btn--sm"
        onClick={onToggle}
        title={
          mode === 'source'
            ? 'Render the preview. Choice is remembered for this mime type.'
            : 'Hide the preview. Choice is remembered for this mime type.'
        }
      >
        {mode === 'source' ? '▶ Render preview' : '◀ Hide preview'}
      </button>
    </div>
  );
}

function PreviewPane({
  documentId,
  mimeType,
  title,
  textContent,
  onSaveContent,
}: {
  documentId: string;
  mimeType: string;
  title: string;
  textContent?: string;
  onSaveContent?: (next: string) => Promise<void>;
}) {
  // 0.1.28 (Stage 2) — render branches consume baselineType.previewRenderer
  // instead of inline mime sniffing. Source of truth is the registry in
  // @captain-tasko/shared/document-types.ts; derivation is pure so this
  // is safe to do on the client.
  //
  // 0.1.27 — HTML has two render modes the operator can flip between:
  //   • 'source' — raw HTML via MarkdownView (editable when role allows;
  //     gated by onSaveContent which the parent supplies only for
  //     documents:edit holders).
  //   • 'preview' — sandboxed iframe (sandbox=""), shows the rendered
  //     page so the operator can identify what they're looking at.
  // PreviewPane defaults to 'source' so we don't lose the editor on
  // page load. Choice is persisted in localStorage so each operator
  // gets their preferred default sticky across docs.
  const baseline = getBaselineType(mimeType);
  const renderer: PreviewRenderer = baseline.previewRenderer;
  const isHtml = renderer === 'sandboxed-iframe';
  const isPdf = renderer === 'pdf-iframe';
  const isImage = renderer === 'image';
  // #125 — docx inline render: depend on the pre-converted HTML living
  // in documents.content (server-side mammoth at upload time).
  const isDocx = renderer === 'docx-html' && isRenderableDocx(mimeType, textContent);
  // Textual covers markdown, source-with-highlight (YAML/JSON/code), and
  // plain-text. All flow through MarkdownView (with wrap-for-preview for
  // non-markdown source). HTML source-mode also re-uses MarkdownView
  // when htmlMode === 'source'.
  const isTextual = (renderer === 'markdown' || renderer === 'source-with-highlight' || renderer === 'plain-text');
  const url = `/api/v1/documents/${documentId}/content`;

  // For non-markdown textual content, wrap in a fenced code block so
  // MarkdownView's rehype-highlight pipeline syntax-colors it. Markdown
  // and plain text pass through unmodified so frontmatter handling and
  // paragraph rendering keep working.
  const previewContent = textContent != null ? wrapForPreview(textContent, mimeType) : textContent;
  // Edit mode in MarkdownView writes the textarea value straight back to
  // disk via PATCH /documents/:id { content }. Wrapping would corrupt
  // non-markdown source on save — disable edit for those types until we
  // build a dedicated editor.
  // 0.1.27 — HTML source is editable as plain text. The actual write
  // permission still comes from onSaveContent being non-null (parent
  // gates that on documents:edit). If the operator lacks the cap, the
  // editor stays read-only; if they have it, they can edit raw HTML.
  const editable = mimeType === 'text/markdown' || mimeType === 'text/plain' || isHtml;

  // 0.1.27 — Sticky per-operator preference for HTML rendering mode in
  // the PreviewPane. Default 'source' so the editor stays visible on
  // first load. The operator can flip to 'preview' to identify the
  // page visually; the choice is remembered in localStorage.
  const [htmlMode, setHtmlMode] = useState<'source' | 'preview'>(() => {
    if (typeof window === 'undefined') return 'source';
    return (window.localStorage.getItem('ct.htmlMode.previewPane') as 'source' | 'preview') ?? 'source';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('ct.htmlMode.previewPane', htmlMode);
    }
  }, [htmlMode]);

  // 0.1.33 — Generalize the source/preview toggle to all heavy renderers
  // (PDF, image, docx-html). Default is 'source' — show a placeholder
  // instead of fetching the file / spinning up the renderer. Operator
  // flips to 'preview' once for a mime; from then on every doc of that
  // mime auto-previews on the PreviewPane. Per-mime + per-surface key.
  const renderModeKey = `ct.renderMode.${mimeType}.previewPane`;
  const [renderMode, setRenderMode] = useState<'source' | 'preview'>(() => {
    if (typeof window === 'undefined') return 'source';
    return (window.localStorage.getItem(renderModeKey) as 'source' | 'preview') ?? 'source';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(renderModeKey, renderMode);
    }
  }, [renderMode, renderModeKey]);

  // CTSK-120 v2 (operator smoke feedback on the first CTSK-402 pass,
  // 2026-08-19) — the "match a reference panel, cap at 640px" pick
  // read as too short in practice (129-page PDF, same complaint on
  // markdown). Superseded: minHeight is now a full viewport (grows
  // freely past that for long content — no cap at all), plus an
  // Expand/Fullscreen toggle for focused reading, mirroring
  // TimelineLane's CTSK-262 pattern (position:fixed inset:0 overlay,
  // ESC to exit, plain session-only useState — never persisted).
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isFullscreen]);

  // CTSK-120 v2 — second control, independent of Expand: "reader pane"
  // pins the preview to the viewport (`position: sticky`) so it stays
  // in view while the operator scrolls the metadata column beside it,
  // instead of scrolling away with the page. Long content scrolls
  // *inside* the pane rather than growing it. This is a standing
  // preference (not a momentary state like fullscreen), so it
  // persists to localStorage like the other PreviewPane toggles above
  // (htmlMode, renderMode). Ignored while fullscreen — the overlay
  // already fills the viewport, sticky positioning has nothing to do
  // there.
  const [readerPinned, setReaderPinned] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('ct.readerPinned.previewPane') === '1';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('ct.readerPinned.previewPane', readerPinned ? '1' : '0');
    }
  }, [readerPinned]);
  const isPinned = readerPinned && !isFullscreen;

  return (
    // Normal (non-fullscreen, non-pinned) state: height: 100% +
    // minHeight: 100vh means the pane is always at least one full
    // screen tall even on a short doc, and grows past that with no
    // cap for long content (DetailsTab's grid `align-items: stretch`
    // still ties it to the left column when the left column is
    // taller). Pinned state swaps to `position: sticky` (needs
    // `alignSelf: start` — a stretched grid item can't stick, it's
    // already stretched to the row's full height) with an explicit
    // viewport-relative height so it has a bounded box to stick
    // *within*; its own content area scrolls internally instead of
    // the pane growing. Fullscreen state breaks out of the grid
    // entirely via .docdetail-preview-fullscreen (position: fixed;
    // inset: 0) — see globals.css.
    <section
      className={isFullscreen ? 'docdetail-preview-fullscreen' : undefined}
      style={{
        background: 'var(--surface-raised)',
        border: isFullscreen ? 'none' : '1px solid var(--border-subtle)',
        borderRadius: isFullscreen ? 0 : 'var(--radius-md)',
        padding: isPdf || isImage ? 0 : 20,
        height: isPinned ? 'calc(100vh - var(--space-8))' : '100%',
        minHeight: (isFullscreen || isPinned) ? undefined : '100vh',
        maxHeight: isPinned ? 'calc(100vh - var(--space-8))' : undefined,
        display: 'flex',
        flexDirection: 'column',
        position: isFullscreen ? undefined : (isPinned ? 'sticky' : 'relative'),
        top: isPinned ? 'var(--space-4)' : undefined,
        alignSelf: isPinned ? 'start' : undefined,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: isPdf || isImage ? '12px 16px' : 0,
          marginBottom: isPdf || isImage ? 0 : 12,
          borderBottom: isPdf || isImage ? '1px solid var(--border-subtle)' : 'none',
          flex: '0 0 auto',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          Rendered preview
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', textTransform: 'lowercase' }}>
            ({mimeType})
          </span>
        </h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Read-only</span>
          {(isPdf || isImage) && (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              style={{ fontSize: 12 }}
              title="Open in a new tab"
            >
              ↗
            </a>
          )}
          {/* CTSK-120 v2 — reader-pane pin. Independent of Expand:
              sticky-positions the pane instead of taking over the
              viewport. Disabled while fullscreen (nothing to pin to —
              the overlay already fills the screen). */}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setReaderPinned(!readerPinned)}
            disabled={isFullscreen}
            aria-pressed={isPinned}
            aria-label={readerPinned ? 'Unpin preview' : 'Pin preview to viewport while scrolling'}
            title={readerPinned ? 'Unpin preview' : 'Pin preview to viewport while scrolling'}
            style={{ padding: '2px 6px' }}
          >
            {readerPinned ? <PinOff size={14} aria-hidden="true" /> : <Pin size={14} aria-hidden="true" />}
          </button>
          {/* CTSK-120 v2 — same Maximize2/Minimize2 pattern as
              TimelineLane's viz Expand toggle. Available for every
              renderer (PDF, image, markdown, HTML, docx), not just
              PDF/image, since the "too short" feedback applied to
              markdown too. */}
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => setIsFullscreen(!isFullscreen)}
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Expand preview'}
            title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Expand to fullscreen'}
            style={{ padding: '2px 6px' }}
          >
            {isFullscreen ? <Minimize2 size={14} aria-hidden="true" /> : <Maximize2 size={14} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* 0.1.29 — AI-prompt-injection warning. Surfaces when the baseline
          type's security floor flags the content as LLM-readable. The
          LLM-call-site gate (Slice 4 / Stage 4 of the AI guard FR) will
          intercept actual feeds into chunker / summarizer / classifier; the
          chip is the visible half so operators see it before they ever
          consider an LLM action. */}
      {baseline.security.aiPromptInjectionGuard && <AiGuardChip />}

      {/* 0.1.33 — PDF / image / docx render only when renderMode === 'preview'.
          Default is 'source', which shows PreviewPlaceholder. Once the
          operator opts in, the choice persists per-mime in localStorage
          so subsequent PDFs/images/etc. auto-render. */}

      {/* PDF — browser-native viewer via iframe; fills the column */}
      {isPdf && (
        <RenderModeBar
          mode={renderMode}
          nudge={renderMode === 'source' ? 'PDF preview hidden' : undefined}
          onToggle={() => setRenderMode(renderMode === 'source' ? 'preview' : 'source')}
        />
      )}
      {isPdf && renderMode === 'preview' && (
        <iframe
          src={url + '#view=FitH'}
          title={title}
          style={{
            width: '100%',
            flex: 1,
            minHeight: 0,
            border: 'none',
            display: 'block',
          }}
        />
      )}

      {/* Image — direct embed; container fills, image fits */}
      {isImage && (
        <RenderModeBar
          mode={renderMode}
          nudge={renderMode === 'source' ? 'Image preview hidden' : undefined}
          onToggle={() => setRenderMode(renderMode === 'source' ? 'preview' : 'source')}
        />
      )}
      {isImage && renderMode === 'preview' && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            placeItems: 'center',
            padding: 16,
            background: 'var(--surface-sunken)',
            overflow: 'hidden',
          }}
        >
          <img
            src={url}
            alt={title}
            style={{
              maxWidth: '100%',
              maxHeight: '100%',
              objectFit: 'contain',
              borderRadius: 4,
            }}
          />
        </div>
      )}

      {/* Textual content via shared MarkdownView — handles frontmatter
          stripping, consistent styling, and the edit-toggle affordance.
          For non-markdown textual content (YAML, JSON, XML, code) the
          body has been pre-wrapped in a fenced code block above so
          rehype-highlight syntax-colors it. The wrapper carries the
          flex+overflow so the section header stays pinned and only the
          rendered content scrolls when it overflows the column height. */}
      {(isTextual || (isHtml && htmlMode === 'source')) && !textContent && <SkeletonRows rows={4} />}
      {(isTextual || (isHtml && htmlMode === 'source')) && (isHtml ? textContent : previewContent) != null && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {/* HTML source/edit mode — same MarkdownView path the editor
              uses for markdown/plaintext. The editable affordance is
              gated by onSaveContent (role check lives in the parent). */}
          {isHtml && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => setHtmlMode('preview')}
                title="Show the page as it renders, in a sandboxed iframe"
              >
                ▶ Render preview
              </button>
            </div>
          )}
          <MarkdownView
            content={isHtml ? (textContent ?? '') : (previewContent ?? '')}
            editable={editable && !!onSaveContent}
            onSave={onSaveContent}
            constrainWidth={false}
            hideFrontmatterBanner={!editable}
          />
        </div>
      )}

      {/* #125 — docx render via the mammoth-generated HTML in
          documents.content. Bypasses MarkdownView because the content
          is HTML, not markdown.
          0.1.33 — gated by renderMode like PDF/image. */}
      {isDocx && textContent && (
        <RenderModeBar
          mode={renderMode}
          nudge={renderMode === 'source' ? 'Word document preview hidden' : undefined}
          onToggle={() => setRenderMode(renderMode === 'source' ? 'preview' : 'source')}
        />
      )}
      {isDocx && textContent && renderMode === 'preview' && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <DocxHtmlView content={textContent} />
        </div>
      )}

      {/* 0.1.27 — HTML rendered into a sandboxed iframe inside the
          PreviewPane. Only shows when the operator toggled to 'preview'
          mode (default is 'source' so the editor stays available).
          Sandbox="" disables scripts/forms/popups/top-nav and gives
          the iframe an opaque origin so it can't read CT's cookies.
          The CSP header on /documents/:id/content blocks external
          img/font/style/script fetches so a clipped page can't beacon
          out. Intended as a visual identifier — "what page is this?"
          — not a full reader. */}
      {isHtml && htmlMode === 'preview' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 12px', borderBottom: '1px solid var(--border-subtle)' }}>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => setHtmlMode('source')}
              title="Switch back to the HTML source (editable when role allows)"
            >
              ◀ Source / edit
            </button>
          </div>
          <iframe
            src={url}
            title={title}
            sandbox=""
            referrerPolicy="no-referrer"
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              border: 'none',
              display: 'block',
              background: 'var(--surface-sunken)',
            }}
          />
        </>
      )}

      {/* Fallback for unknown types (legacy Word, etc.) */}
      {!isTextual && !isPdf && !isImage && !isDocx && !isHtml && (
        <div style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
          Inline preview not available for <code>{mimeType}</code>.<br />
          <a href={url} target="_blank" rel="noreferrer">Open in a new tab</a> or use the <strong>Content</strong> tab.
        </div>
      )}
    </section>
  );
}

function ContentTab({ id, mime, title, content, onSaveContent }: {
  id: string;
  mime: string;
  title: string;
  content?: string;
  onSaveContent?: (next: string) => Promise<void>;
}) {
  // 0.1.28 (Stage 2) — render branches consume baselineType.previewRenderer.
  // Same model as PreviewPane: text/html splits between source MarkdownView
  // and sandboxed iframe based on htmlMode; everything else is renderer-
  // driven via the registry.
  //
  // 0.1.27 — text/html in the Content tab also gets a toggle, but
  // unlike the PreviewPane (which defaults to 'source' so the editor is
  // immediately visible) the Content tab defaults to 'preview' — most
  // operators want to see the rendered page here. Both surfaces share
  // independent localStorage keys.
  const baseline = getBaselineType(mime);
  const renderer: PreviewRenderer = baseline.previewRenderer;
  const isHtml = renderer === 'sandboxed-iframe';
  // Source/code branches all flow through MarkdownView for now.
  const isTextual = (renderer === 'markdown' || renderer === 'source-with-highlight' || renderer === 'plain-text');
  // #125 — docx renders from pre-converted HTML in documents.content.
  const isDocx = renderer === 'docx-html' && isRenderableDocx(mime, content);

  const [htmlMode, setHtmlMode] = useState<'source' | 'preview'>(() => {
    if (typeof window === 'undefined') return 'preview';
    return (window.localStorage.getItem('ct.htmlMode.contentTab') as 'source' | 'preview') ?? 'preview';
  });
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('ct.htmlMode.contentTab', htmlMode);
    }
  }, [htmlMode]);
  function print() {
    if (isTextual && content) {
      const win = window.open('', '_blank');
      if (!win) {
        toast.error('Popup blocked. Allow popups for the print preview.');
        return;
      }
      win.document.write(`<!doctype html><html><head><title>${title}</title>
        <style>
          body { font-family: Inter, system-ui, sans-serif; max-width: 720px; margin: 32px auto; padding: 0 16px; line-height: 1.6; }
          pre, code { font-family: ui-monospace, Menlo, monospace; background: #f5f4f1; padding: 2px 4px; border-radius: 3px; }
          pre { padding: 12px; overflow: auto; white-space: pre-wrap; }
          table { border-collapse: collapse; }
          th, td { border: 1px solid #ccc; padding: 4px 8px; }
        </style></head><body><h1>${title}</h1><pre>${
        content.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]!))
      }</pre><script>window.print(); setTimeout(() => window.close(), 500);</script></body></html>`);
      win.document.close();
    } else if (isDocx) {
      // #125 — docx renders as inline HTML in the page; printing the
      // window catches the rendered output. No iframe to delegate to.
      window.print();
    } else {
      const iframe = document.getElementById('doc-content-iframe') as HTMLIFrameElement | null;
      if (iframe?.contentWindow) iframe.contentWindow.print();
      else window.print();
    }
  }

  // ctsk-059 — Content tab now shares chrome with the PreviewPane:
  // single chromed section with a header row (label + mime + actions)
  // and the rendered/embedded content below. Was: a loose action-button
  // row floating above an unlabeled framed section. Bringing both
  // surfaces to the same shape makes the docs page feel of-a-piece.
  const isBinary = !isTextual && !isDocx;

  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 0,
        maxHeight: '82vh',
        overflow: 'auto',
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '12px 16px',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          Content
          <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)', textTransform: 'lowercase' }}>
            ({mime})
          </span>
        </h3>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {/* 0.1.27 — HTML mode toggle. Default 'preview' for the
              Content tab; the operator can flip to 'source' to view /
              edit the raw HTML. Edit affordance respects the same
              onSaveContent gate the other textual paths use, so lower
              roles see read-only source. */}
          {isHtml && (
            <button
              className="btn btn--ghost"
              onClick={() => setHtmlMode(htmlMode === 'preview' ? 'source' : 'preview')}
              title={htmlMode === 'preview' ? 'View HTML source (editable when role allows)' : 'Render the page in a sandboxed preview'}
            >
              {htmlMode === 'preview' ? '◀ Source' : '▶ Preview'}
            </button>
          )}
          <button className="btn btn--ghost" onClick={print} title="Print this content">
            🖨 Print
          </button>
          <a
            className="btn btn--ghost"
            href={`/api/v1/documents/${id}/content`}
            target="_blank"
            rel="noreferrer"
            title="Open the raw bytes in a new browser tab"
          >
            ↗ Open
          </a>
          <a
            className="btn btn--ghost"
            href={`/api/v1/documents/${id}/content`}
            download
            title="Download the original file"
          >
            ⤓ Download
          </a>
        </div>
      </div>

      {/* 0.1.29 — AI guard chip in the Content tab too. Mirrors the
          PreviewPane behavior so the warning is visible wherever the
          operator views the content. */}
      {baseline.security.aiPromptInjectionGuard && <AiGuardChip />}

      {content && (isTextual || (isHtml && htmlMode === 'source')) && (
        <div style={{ padding: 32 }}>
          <MarkdownView
            content={content}
            editable={!!onSaveContent}
            onSave={onSaveContent}
            constrainWidth
          />
        </div>
      )}
      {/* #125 — docx as rendered HTML rather than iframe-to-binary. */}
      {isDocx && content && (
        <div style={{ padding: 32 }}>
          <DocxHtmlView content={content} />
        </div>
      )}
      {/* 0.1.27 — iframe path covers: native binaries (PDF, etc.) and
          HTML when the operator selected 'preview' mode. HTML source
          mode flows through MarkdownView above with edit gating. */}
      {(isBinary && !isHtml) || (isHtml && htmlMode === 'preview') ? (
        <iframe
          id="doc-content-iframe"
          src={`/api/v1/documents/${id}/content`}
          // ctsk-059 — iframe lives inside the chromed section now, so
          // no border / radius of its own; fills the section's available
          // height up to the section's maxHeight cap.
          style={{ width: '100%', height: '72vh', border: 'none', display: 'block' }}
          title={title}
          // 0.1.27 — defense-in-depth for HTML/SVG previews. `sandbox=""`
          // is the strictest setting: no scripts, no forms, no top-nav,
          // no popups, opaque origin so document cookies don't leak.
          // 0.1.33 — RESTRICTED to HTML/SVG/XHTML mimes only. PDFs
          // delegate to the browser's built-in PDF reader which needs
          // same-origin access to fetch the bytes and render zoom /
          // page-nav controls; sandboxing it caused Brave to refuse
          // the inline view and Chromium to render a blank frame.
          // The browser's own PDF viewer is the trust boundary for
          // PDFs — we don't need to layer an iframe sandbox on top.
          sandbox={isHtml ? '' : undefined}
          referrerPolicy="no-referrer"
        />
      ) : null}
    </section>
  );
}

function MetadataTab({ doc, sidecar }: { doc: DocumentListItem; sidecar?: SidecarBody }) {
  // 0.1.20 — layout fix. Previously two `1fr 1fr` columns without
  // `min-width: 0` couldn't shrink below their intrinsic content
  // width: the SYSTEM column's monospace `doc.id` (60+ chars) blew
  // out the column, and the .kv class's grid styling then forced
  // the long values to wrap one character per line. The SIDECAR
  // <pre> ended up squeezed into a narrow column with horizontal
  // overflow.
  //
  // Fix:
  // - Use minmax(0, 1fr) on each column so they can shrink, and
  //   stack at viewports below 1100px so neither column is
  //   forced narrow.
  // - SYSTEM rows: long mono values get word-break: break-all so
  //   they wrap on character boundaries WITHIN the column width.
  // - SIDECAR pre: word-break: break-all + overflow-x: auto so
  //   long JSON values either wrap or scroll, not push the layout.
  const monoLong = {
    fontFamily: 'var(--font-mono)' as const,
    wordBreak: 'break-all' as const,
    overflowWrap: 'anywhere' as const,
  };
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
        gap: 24,
        minWidth: 0,
      }}
    >
      <section style={{ minWidth: 0 }}>
        <h3 style={{ fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>System</h3>
        <dl className="kv" style={{ minWidth: 0 }}>
          <dt>Document ID</dt><dd><code style={monoLong}>{doc.id}</code></dd>
          <dt>Source path</dt><dd style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{doc.sourcePath}</dd>
          <dt>Sidecar path</dt><dd style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{doc.sidecarPath}</dd>
          <dt>Content hash</dt><dd><code style={monoLong}>{doc.contentHash}</code></dd>
          <dt>ETag</dt><dd><code style={monoLong}>{doc.etag}</code></dd>
          <dt>Original filename</dt><dd style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{doc.originalFilename ?? '—'}</dd>
          <dt>MIME type</dt><dd>{doc.mimeType}</dd>
          <dt>Size</dt><dd>{formatBytes(doc.sizeBytes)}</dd>
        </dl>
      </section>
      <section style={{ minWidth: 0 }}>
        <h3 style={{ fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>Sidecar</h3>
        {!sidecar || Object.keys(sidecar).length === 0 ? (
          <p style={{ color: 'var(--text-muted)' }}>Sidecar empty. Run a pipeline to populate it.</p>
        ) : (
          <pre
            style={{
              background: 'var(--surface-sunken)',
              padding: 12,
              borderRadius: 4,
              fontSize: 12,
              maxHeight: 480,
              overflow: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              overflowWrap: 'anywhere',
              minWidth: 0,
            }}
          >
            {JSON.stringify(sidecar, null, 2)}
          </pre>
        )}
      </section>
    </div>
  );
}

// 0.1.24 (Stage 2c of document-relationships FR) — recursive crawl
// tree widget. Queries GET /documents/:id/tree?context=parent_of
// (uses the recursive CTE endpoint) and renders the hierarchy as
// nested rows. Hidden when the doc has no parent_of outbound
// links (i.e. it's a leaf, not a crawl seed / interior parent).
interface TreeNode {
  id: string;
  parentId: string | null;
  depth: number;
  title: string;
  slug: string | null;
  status: string;
  health: string;
}

function CrawlTreeSection({
  documentId,
  onNavigate,
}: {
  documentId: string;
  onNavigate: (p: string) => void;
}) {
  const treeQ = useQuery({
    queryKey: ['document-tree', documentId, 'parent_of'],
    queryFn: () =>
      apiSingle<{ rootId: string; context: string; maxDepth: number; nodes: TreeNode[]; truncated: boolean }>(
        `/documents/${documentId}/tree?context=parent_of&maxDepth=10`,
      ),
  });

  const allNodes = treeQ.data?.nodes ?? [];
  // The root (this doc) is always returned; show the tree only when
  // there's at least one descendant.
  if (allNodes.length <= 1) return null;

  // Bucket children by parentId for efficient rendering.
  const byParent = new Map<string, TreeNode[]>();
  for (const n of allNodes) {
    if (n.parentId == null) continue;
    const bucket = byParent.get(n.parentId) ?? [];
    bucket.push(n);
    byParent.set(n.parentId, bucket);
  }

  function renderChildren(parentId: string): React.ReactNode {
    const kids = byParent.get(parentId) ?? [];
    if (kids.length === 0) return null;
    return (
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, paddingLeft: 16, borderLeft: '1px dashed var(--border-subtle)' }}>
        {kids.map((kid) => (
          <li key={kid.id} style={{ padding: '2px 0' }}>
            <button
              type="button"
              onClick={() => onNavigate(`/documents/${kid.id}`)}
              title={kid.title}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--accent-primary)',
                cursor: 'pointer',
                padding: 0,
                fontSize: 12,
                textAlign: 'left',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                display: 'block',
              }}
            >
              {kid.title}
            </button>
            {renderChildren(kid.id)}
          </li>
        ))}
      </ul>
    );
  }

  const descendantCount = allNodes.length - 1; // root excluded
  return (
    <section
      style={{
        background: 'var(--surface-raised)',
        padding: 16,
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.06em' }}>
          Crawl tree
          <span style={{ marginLeft: 8, fontWeight: 400, color: 'var(--text-muted)' }}>
            ({descendantCount} descendant{descendantCount === 1 ? '' : 's'})
          </span>
        </h3>
        <button
          type="button"
          onClick={() => onNavigate(`/documents?related=${documentId}&relatedContext=parent_of`)}
          title="Open direct children in the Library with full filters"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--accent-primary)',
            fontSize: 11,
            padding: 0,
            textDecoration: 'underline',
          }}
        >
          Open direct children
        </button>
      </div>
      {treeQ.data?.truncated && (
        <div style={{
          background: 'color-mix(in srgb, #F59E0B 14%, var(--surface-raised))',
          color: '#854F0B',
          padding: '6px 10px',
          borderRadius: 4,
          fontSize: 11,
          marginBottom: 8,
        }}>
          Tree truncated at maxDepth=10. Deeper descendants not shown.
        </div>
      )}
      {renderChildren(documentId)}
    </section>
  );
}

function CustomPropertyField({
  property,
  value,
  provenance,
  onChange,
}: {
  property: CustomProperty;
  value: unknown;
  /** FR #198 — when set, render the provenance meta row under the
   *  input. Absent for never-written properties or when the
   *  provenance fetch hasn't returned yet. */
  provenance?: CustomPropertyProvenanceEntry;
  onChange: (next: unknown) => void;
}) {
  const labelEl = (
    <label htmlFor={`cp-${property.key}`} style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', justifyContent: 'space-between' }}>
      <span>
        {property.name}
        {property.required && <span style={{ color: 'var(--danger-primary)' }}> *</span>}
      </span>
      <code style={{ fontFamily: 'var(--font-mono)', fontSize: 10 }}>{property.type}</code>
    </label>
  );
  // FR #198 option B — meta row rendered AFTER the input by the
  // caller. We can't add it inside the switch because each input
  // case returns a single element; we capture it once here and let
  // the wrapper render it.
  const provenanceEl = provenance ? <CustomPropertyProvenanceChip entry={provenance} /> : null;

  const inputId = `cp-${property.key}`;
  const baseStyle = { width: '100%', padding: 4, border: '1px solid var(--border-strong)', borderRadius: 4, fontSize: 13 };

  let input: React.ReactNode;
  switch (property.type) {
    case 'text':
    case 'url':
      input = (
        <input
          id={inputId}
          type={property.type === 'url' ? 'url' : 'text'}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          style={baseStyle}
        />
      );
      break;
    case 'number':
      input = (
        <input
          id={inputId}
          type="number"
          value={(value as number | string) ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
          style={baseStyle}
        />
      );
      break;
    case 'date': {
      // 0.1.18 — the CP-projection coercer normalizes any input
      // (Date instance, ISO string, epoch ms) to a full ISO 8601
      // string via toISOString(), e.g. "2026-06-04T17:53:00.401Z".
      // HTML <input type="date"> only accepts bare YYYY-MM-DD;
      // anything with a "T" / "Z" gets rejected and the input
      // renders empty. Slice for display; onChange still emits a
      // YYYY-MM-DD which the coercer round-trips back through
      // toISOString() on the next save (operator-edited dates
      // default to midnight UTC, consistent with prior behavior).
      //
      // CTSK-128 (2026-07-23): for the datetime type, use the
      // sibling `case 'datetime'` block below which preserves
      // sub-day precision.
      const raw = (value as string | null | undefined) ?? '';
      const dateOnly = typeof raw === 'string' && raw.length >= 10 ? raw.slice(0, 10) : '';
      input = (
        <input
          id={inputId}
          type="date"
          value={dateOnly}
          onChange={(e) => onChange(e.target.value || null)}
          style={baseStyle}
        />
      );
      break;
    }
    case 'datetime': {
      // CTSK-128 (2026-07-23): datetime CP type — precision-preserving
      // sibling of 'date'. Renders <input type="datetime-local"> which
      // accepts and emits "YYYY-MM-DDTHH:mm" (local TZ, no timezone
      // suffix). Coercer at read + save time normalizes back to full
      // ISO 8601 UTC via toISOString(); operator edits round-trip
      // WITHOUT losing sub-day precision (unlike 'date' which slices
      // to YYYY-MM-DD and reconstitutes at midnight UTC).
      //
      // Storage shape: same as 'date' — ISO 8601 UTC string in
      // documents.customProperties. The type distinction is:
      //
      //   - 'date'      → operator edits are day-precision; time
      //                   portion resets to 00:00:00 UTC on save
      //   - 'datetime'  → operator edits preserve time to the
      //                   minute; sub-minute precision (seconds, ms)
      //                   from producer imports round-trips too
      //
      // Migration path: change a CP's type from 'date' → 'datetime'
      // without touching stored values (column is text). Existing
      // rows re-hydrate with their full ISO timestamp; operators
      // can now edit hours + minutes instead of just the date.
      // hyper-scanno bundles' `*_at` CPs (crawl_started_at,
      // scan_finished_at, etc.) are the target flip per the ticket.
      //
      // Input value format for `<input type="datetime-local">`:
      // YYYY-MM-DDTHH:mm (no seconds, no timezone). We derive from
      // the stored ISO by slicing to length 16. The browser echoes
      // back the same format on change; new Date(that_string)
      // interprets it as LOCAL time, which is the operator's intent
      // when they type a time into the field.
      const raw = (value as string | null | undefined) ?? '';
      const localDatetime = typeof raw === 'string' && raw.length >= 16
        ? raw.slice(0, 16)  // e.g. "2026-07-23T14:30" from "2026-07-23T14:30:45.123Z"
        : '';
      input = (
        <input
          id={inputId}
          type="datetime-local"
          value={localDatetime}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return onChange(null);
            // Convert "YYYY-MM-DDTHH:mm" (local) → ISO 8601 UTC.
            // The Date constructor interprets bare local strings as
            // local TZ; toISOString() emits the UTC-normalized form
            // the coercer + storage layer both expect.
            const dt = new Date(v);
            if (Number.isNaN(dt.getTime())) return onChange(null);
            onChange(dt.toISOString());
          }}
          style={baseStyle}
        />
      );
      break;
    }
    case 'boolean':
      input = (
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <input
            id={inputId}
            type="checkbox"
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
          />{' '}
          {value === true ? 'Yes' : 'No'}
        </label>
      );
      break;
    case 'select':
      input = (
        <select
          id={inputId}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          style={baseStyle}
        >
          <option value="">— Choose —</option>
          {property.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      );
      break;
    case 'document_link':
    case 'user':
      input = (
        <input
          id={inputId}
          type="text"
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder={property.type === 'document_link' ? 'doc_…' : 'user id'}
          style={baseStyle}
        />
      );
      break;
    default:
      input = <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>Unsupported type</span>;
  }

  return (
    <div>
      {labelEl}
      <div style={{ marginTop: 4 }}>{input}</div>
      {/* FR #198 option B — provenance meta row under the value
          (label row stays minimal: just name + type). Skipped when
          the value has never been set or provenance hasn't loaded. */}
      {provenanceEl}
      {property.description && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{property.description}</div>
      )}
    </div>
  );
}

function PermissionsTab({ doc }: { doc: DocumentListItem }) {
  return (
    <div>
      <p style={{ color: 'var(--text-muted)' }}>
        Owner: <code style={{ fontFamily: 'var(--font-mono)' }}>{doc.permissions.ownerId ?? '—'}</code>
      </p>
      <p style={{ color: 'var(--text-muted)' }}>
        View / Edit lists populated by the permissions editor coming in Phase 7.
      </p>
    </div>
  );
}

// ─── Helpers for #117 (textual non-text/* MIMEs + preview wrapping) ─────────

/**
 * Returns true for any MIME we know how to render as text content in the
 * document viewer. Covers everything under text/*, plus the
 * application/* MIMEs that are textual by nature (YAML, JSON, XML, code).
 * Used in two places — the content-fetch gate at the top of DocumentDetail
 * and the PreviewPane's render switch.
 */
function isTextualMime(mime: string): boolean {
  if (mime.startsWith('text/')) return true;
  return (
    mime === 'application/json' ||
    mime === 'application/yaml' ||
    mime === 'application/x-yaml' ||
    mime === 'application/xml' ||
    mime === 'application/javascript' ||
    mime === 'application/typescript' ||
    mime === 'application/x-python' ||
    mime === 'application/x-sh' ||
    mime === 'application/x-shellscript'
  );
}

// #125 — docx is rendered as pre-converted HTML stored in
// documents.content (mammoth.js convertToHtml runs at upload time).
// Treat it as a distinct render path because the content is HTML, not
// markdown — sending it through MarkdownView would either escape it as
// text or fight the sanitizer's tag whitelist.
const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
function isRenderableDocx(mime: string, content?: string | null): boolean {
  return mime === DOCX_MIME && !!content && content.trim().length > 0;
}

// Defense-in-depth: strip any tags / attributes mammoth would not
// normally emit but could in principle (raw-html mode, malformed docx,
// future mammoth versions). Belt + suspenders over mammoth's already
// restricted default output.
function sanitizeDocxHtml(raw: string): string {
  if (typeof document === 'undefined') return raw; // SSR no-op
  const doc = new DOMParser().parseFromString(raw, 'text/html');
  const DANGEROUS = ['script', 'iframe', 'object', 'embed', 'link', 'style', 'meta', 'base'];
  for (const sel of DANGEROUS) {
    doc.querySelectorAll(sel).forEach((el) => el.remove());
  }
  doc.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const n = attr.name.toLowerCase();
      if (n.startsWith('on') || n === 'srcdoc' || n === 'formaction') {
        el.removeAttribute(attr.name);
      }
      // javascript: / data: URLs in href/src
      if ((n === 'href' || n === 'src') && /^\s*(javascript|data):/i.test(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
  });
  return doc.body.innerHTML;
}

function DocxHtmlView({ content }: { content: string }) {
  const safe = useMemo(() => sanitizeDocxHtml(content), [content]);
  return (
    <div
      className="markdown-body"
      // mammoth output is paragraphs / headings / tables / lists / basic
      // inline formatting only. We render into the existing .markdown-body
      // typography so docx previews match markdown previews visually.
      dangerouslySetInnerHTML={{ __html: safe }}
    />
  );
}

/**
 * Map a MIME to a highlight.js language tag. Returns empty string when
 * we don't have a sensible mapping; the caller treats empty as "render
 * as plain text" (no fence wrapping).
 */
function languageForMime(mime: string): string {
  if (mime === 'application/yaml' || mime === 'application/x-yaml' || mime === 'text/yaml') return 'yaml';
  if (mime === 'application/json') return 'json';
  if (mime === 'application/xml' || mime === 'text/xml') return 'xml';
  if (mime === 'text/html') return 'html';
  if (mime === 'text/css') return 'css';
  if (mime === 'application/javascript' || mime === 'text/javascript') return 'javascript';
  if (mime === 'application/typescript' || mime === 'text/x-typescript') return 'typescript';
  if (mime === 'application/x-python' || mime === 'text/x-python') return 'python';
  if (mime === 'application/x-sh' || mime === 'application/x-shellscript') return 'bash';
  return '';
}

/**
 * Wrap textual content in a fenced code block so MarkdownView's
 * rehype-highlight pipeline syntax-colors it. text/markdown and
 * text/plain pass through unmodified so markdown rendering and plain
 * paragraph layout still work.
 */
function wrapForPreview(content: string, mime: string): string {
  if (mime === 'text/markdown' || mime === 'text/plain') return content;
  const lang = languageForMime(mime);
  // Even when we don't have a specific language, wrap in an unlabeled
  // fence so the content lands in a monospace code block rather than
  // being interpreted as markdown prose (e.g. a JSON file with `*` in
  // a string shouldn't become bold).
  return `\`\`\`${lang}\n${content}\n\`\`\``;
}

// ctsk-062 — Linked Documents panel on the document detail view.
// (Renamed from "Related Documents" to disambiguate from the
// auto-derived DocumentGroupPanel, which keeps that heading.)
// Reads `/documents/:id/links`, mutates via POST/DELETE on the same
// endpoint. Add affordance is a typeahead picker hitting the existing
// documents list endpoint with `q=<text>` for a one-trip search.
function LinkedDocumentsSection({
  documentId,
  onNavigate,
}: {
  documentId: string;
  onNavigate: (p: string) => void;
}) {
  const qc = useQueryClient();
  const [unlocked, setUnlocked] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pendingContext, setPendingContext] =
    useState<(typeof DOCUMENT_LINK_CONTEXTS)[number]>('related');

  const linksQ = useQuery({
    queryKey: ['document-links', documentId],
    queryFn: () => apiList<RelatedDocument>(`/documents/${documentId}/links`),
  });
  const links = linksQ.data?.data ?? [];

  // Typeahead — only fires when the picker is open and there's >=2
  // chars so we don't spam the API on every keystroke.
  const searchQ = useQuery({
    queryKey: ['documents-search', pickerQuery],
    queryFn: () =>
      apiList<DocumentListItem>('/documents', { query: { q: pickerQuery, limit: 10 } }),
    enabled: pickerOpen && pickerQuery.trim().length >= 2,
    staleTime: 5_000,
  });

  const addLink = useMutation({
    mutationFn: (args: { targetId: string; context: string }) =>
      apiSingle<{ ok: boolean }>(`/documents/${documentId}/links`, {
        method: 'POST',
        body: { targetId: args.targetId, context: args.context },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document-links', documentId] });
      setPickerOpen(false);
      setPickerQuery('');
      toast.success('Linked');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeLink = useMutation({
    mutationFn: (args: { targetId: string; context: string }) =>
      apiFetch(`/documents/${documentId}/links/${args.targetId}?context=${args.context}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['document-links', documentId] });
      toast.success('Unlinked');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <CollapsiblePanel
      storageKey="docdetail:linked-documents"
      title="Linked documents"
      badge={linksQ.isLoading ? undefined : `(${links.length})`}
      defaultCollapsed
      persist={false}
      headerExtras={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* 0.1.23 Stage 2b — Open in library navigates to
              /documents?related=<id> for full sort / filter / table
              view of the linked docs. Only renders when there's
              something to open. */}
          {links.length > 0 && (
            <button
              type="button"
              onClick={() => onNavigate(`/documents?related=${documentId}`)}
              title="Open all linked documents in the Library with full filters"
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--accent-primary)',
                fontSize: 11,
                padding: 0,
                textDecoration: 'underline',
              }}
            >
              Open in library
            </button>
          )}
          <button
            type="button"
            aria-label={unlocked ? 'Lock linked documents' : 'Edit linked documents'}
            onClick={() => setUnlocked((v) => !v)}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: unlocked ? 'var(--accent-primary)' : 'var(--text-muted)',
              fontSize: 13,
              padding: 2,
            }}
          >
            {unlocked ? '🔒' : '✎'}
          </button>
        </div>
      }
    >

      {linksQ.isLoading ? (
        <SkeletonRows rows={2} />
      ) : links.length === 0 ? (
        <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: 0 }}>
          {unlocked ? 'No links yet. Use Add link below.' : 'No linked documents.'}
        </p>
      ) : (
        // 0.1.21 Stage 2 — group links by context so crawl pages with
        // many parents / children / siblings render usefully. Context
        // groups appear in DOCUMENT_LINK_CONTEXTS order, so the
        // editorial verbs (related / supersedes / references / etc.)
        // sort BEFORE the crawl-tree verbs (parent_of / child_of /
        // sibling / seed_of / seeded_from) — matches the constants
        // file's declared order.
        (() => {
          const grouped = new Map<string, RelatedDocument[]>();
          for (const ctx of DOCUMENT_LINK_CONTEXTS) grouped.set(ctx, []);
          for (const l of links) {
            const bucket = grouped.get(l.context) ?? [];
            bucket.push(l);
            grouped.set(l.context, bucket);
          }
          const nonEmpty = Array.from(grouped.entries()).filter(([, v]) => v.length > 0);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {nonEmpty.map(([ctx, items]) => (
                <div key={ctx} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{
                    fontSize: 10,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '.06em',
                    fontWeight: 600,
                    marginBottom: 2,
                  }}>
                    {DOCUMENT_LINK_LABELS[ctx as (typeof DOCUMENT_LINK_CONTEXTS)[number]] ?? ctx}
                    {items.length > 1 && (
                      <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontWeight: 400 }}>
                        ({items.length})
                      </span>
                    )}
                  </div>
                  <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {items.map((l) => (
                      <li
                        key={`${l.id}::${l.context}`}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '2px 0',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => onNavigate(`/documents/${l.id}`)}
                          title={l.title}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: 'var(--accent-primary)',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 13,
                            textAlign: 'left',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            flex: 1,
                          }}
                        >
                          {l.title}
                        </button>
                        {unlocked && (
                          <button
                            type="button"
                            aria-label={`Remove link to ${l.title}`}
                            onClick={() => removeLink.mutate({ targetId: l.id, context: l.context })}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              cursor: 'pointer',
                              color: 'var(--text-muted)',
                              fontSize: 14,
                              padding: 0,
                            }}
                          >
                            ×
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          );
        })()
      )}

      {unlocked && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {!pickerOpen ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setPickerOpen(true)}
              style={{ fontSize: 12 }}
            >
              + Add link
            </button>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  value={pendingContext}
                  onChange={(e) =>
                    setPendingContext(e.target.value as (typeof DOCUMENT_LINK_CONTEXTS)[number])
                  }
                  style={{
                    padding: '4px 6px',
                    fontSize: 12,
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                    background: 'var(--surface-sunken)',
                    color: 'inherit',
                  }}
                >
                  {DOCUMENT_LINK_CONTEXTS.filter((c) => !c.endsWith('_by')).map((c) => (
                    <option key={c} value={c}>
                      {DOCUMENT_LINK_LABELS[c]}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  autoFocus
                  placeholder="Type to search documents…"
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '4px 8px',
                    fontSize: 12,
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    setPickerOpen(false);
                    setPickerQuery('');
                  }}
                  className="btn btn--ghost"
                  style={{ fontSize: 12 }}
                >
                  Cancel
                </button>
              </div>
              {pickerQuery.trim().length >= 2 && (
                <ul
                  style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    maxHeight: 180,
                    overflowY: 'auto',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 4,
                  }}
                >
                  {(searchQ.data?.data ?? [])
                    .filter((d) => d.id !== documentId)
                    .map((d) => (
                      <li key={d.id}>
                        <button
                          type="button"
                          onClick={() =>
                            addLink.mutate({ targetId: d.id, context: pendingContext })
                          }
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px 8px',
                            fontSize: 12,
                            width: '100%',
                            textAlign: 'left',
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {d.title}
                        </button>
                      </li>
                    ))}
                  {(searchQ.data?.data ?? []).length === 0 && !searchQ.isLoading && (
                    <li
                      style={{
                        padding: '6px 8px',
                        color: 'var(--text-muted)',
                        fontSize: 12,
                      }}
                    >
                      No matches.
                    </li>
                  )}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </CollapsiblePanel>
  );
}
